import { AnimatePresence, motion } from "framer-motion";
import { FileDown, Link2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { useFolderDropClaim } from "@/components/files/use-folder-upload";
import { useUpload } from "@/hooks/use-upload";
import { useWebIngest } from "@/hooks/use-web-ingest";
import {
  collectEntries,
  MAX_UPLOAD_DEPTH,
  MAX_UPLOAD_FILES,
  type FileSystemEntryLike,
} from "@/lib/dropzone-entries";

/** Full-window drop target: drag a file anywhere, the observatory opens. */
export function GlobalDropzone() {
  const upload = useUpload();
  const ingestWeb = useWebIngest();
  const [depth, setDepth] = useState(0);
  const [kind, setKind] = useState<"files" | "link">("files");
  // A folder row under the cursor takes precedence: it uploads INTO that
  // folder, and covering it with a full-screen "into the atlas" target would
  // both hide the thing being aimed at and file the drop at the root.
  const claimed = useFolderDropClaim((s) => s.claimed);

  useEffect(() => {
    const hasFiles = (e: DragEvent) => e.dataTransfer?.types.includes("Files");
    const hasLink = (e: DragEvent) =>
      e.dataTransfer?.types.includes("text/uri-list") ?? false;
    const enter = (e: DragEvent) => {
      if (hasFiles(e) || hasLink(e)) {
        setKind(hasLink(e) ? "link" : "files");
        setDepth((d) => d + 1);
      }
    };
    const leave = (e: DragEvent) => {
      if (hasFiles(e) || hasLink(e)) setDepth((d) => Math.max(0, d - 1));
    };
    const over = (e: DragEvent) => {
      if (hasFiles(e) || hasLink(e)) e.preventDefault();
    };
    // Traverse a dropped folder's entry tree, then hand the flattened
    // {file, relPath} pairs to the same upload path the picker uses. Warns when
    // the drop was too large / too deep to import whole.
    const ingestEntries = async (entries: FileSystemEntryLike[]) => {
      const { items, truncated, depthTruncated } = await collectEntries(entries);
      if (truncated) {
        toast.warning(`Imported the first ${MAX_UPLOAD_FILES} files`, {
          description:
            "That drop is very large — the rest were left out. Import the remaining folders separately.",
        });
      } else if (depthTruncated) {
        toast.warning("Some deeply nested folders were skipped", {
          description: `Folders more than ${MAX_UPLOAD_DEPTH} levels deep weren't imported.`,
        });
      }
      if (items.length) await upload(items);
    };

    const drop = (e: DragEvent) => {
      if (!hasFiles(e) && !hasLink(e)) return;
      setDepth(0);
      // The row's own handler stops propagation, so this is belt and braces for
      // engines that dispatch the window listener first.
      if (useFolderDropClaim.getState().claimed) {
        useFolderDropClaim.getState().release();
        return;
      }
      e.preventDefault();
      const dt = e.dataTransfer;
      if (dt && hasFiles(e)) {
        // A folder drag only reveals its structure through webkitGetAsEntry();
        // dataTransfer.files flattens it away. The items list is live ONLY
        // during dispatch, so snapshot the entries synchronously, then traverse
        // them off the event loop.
        const entries: FileSystemEntryLike[] = [];
        for (let i = 0; i < dt.items.length; i++) {
          const item = dt.items[i];
          if (!item || item.kind !== "file") continue;
          const entry = item.webkitGetAsEntry?.();
          if (entry) entries.push(entry as unknown as FileSystemEntryLike);
        }
        if (entries.length) {
          void ingestEntries(entries);
          return;
        }
        // No entry API (older engine): fall back to the flat file list.
        if (dt.files.length) {
          void upload(dt.files);
          return;
        }
      }
      const url = dt
        ?.getData("text/uri-list")
        .split(/\r?\n/)
        .find((line) => line && !line.startsWith("#"));
      if (url) void ingestWeb(url);
    };
    window.addEventListener("dragenter", enter);
    window.addEventListener("dragleave", leave);
    window.addEventListener("dragover", over);
    window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragenter", enter);
      window.removeEventListener("dragleave", leave);
      window.removeEventListener("dragover", over);
      window.removeEventListener("drop", drop);
    };
  }, [ingestWeb, upload]);

  return (
    <AnimatePresence>
      {depth > 0 && !claimed && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="pointer-events-none fixed inset-0 z-50 grid place-items-center bg-background/85 backdrop-blur-sm"
        >
          <div className="grid h-56 w-56 place-items-center rounded-full border-2 border-dashed border-primary/70">
            <span className="absolute h-64 w-64 rounded-full border border-primary/20 animate-breathe" />
            <div className="flex flex-col items-center gap-2 text-primary">
              {kind === "link" ? (
                <Link2 className="h-7 w-7" />
              ) : (
                <FileDown className="h-7 w-7" />
              )}
              <span className="font-display text-lg italic">
                {kind === "link" ? "chart this link" : "into the atlas"}
              </span>
              <span className="font-mono text-2xs text-muted-foreground">
                {kind === "link" ? "extract · summarize · connect" : "pdf · docx · pptx · html · code · md · txt"}
              </span>
            </div>
          </div>
          <p className="absolute bottom-10 max-w-sm px-6 text-center font-mono text-2xs text-muted-foreground">
            Avoid classified, medical, credential, or other highly sensitive files —
            content is processed by third-party AI providers.
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
